/**
 * CreateQuoteRequestDialog — modal de création QR avec multi-select assureurs
 * (Chunk 9.L.8).
 *
 * Backend : `POST /v1/quote-requests` (9.L.7).
 * Création atomique QR draft + 0..N recipients sélectionnés (insurer_code).
 *
 * UX :
 *   - `InsurerMultiSelect` (registre dynamique tenant, 9.V.2c-2b) — seuls les
 *     assureurs actifs sont proposés ; parité `Step3Coverage` intake_mvp.
 *   - `title` optionnel (max 200 chars).
 *   - `expected_response_by` date picker optionnel (`<input type="date">` natif
 *     → conversion ISO datetime UTC `T12:00:00.000Z` parité SendQuoteRequestDialog 9.L.6).
 *   - Esc + backdrop click → close (sauf pendant submit).
 *   - Submit toujours actif (project_id auto, autres champs optionnels — backend
 *     accepte QR draft sans recipients ni title).
 *
 * Erreurs API (bilingues via `getApiErrorMessage`) :
 *   - 404 QUOTE_REQUESTS_PROJECT_NOT_FOUND  → bandeau red + close + parent refetch
 *   - 409 QUOTE_REQUEST_RECIPIENTS_DUPLICATE → bandeau red (duplicate in body recipients)
 *   - 400 VALIDATION_ERROR                  → erreur inline (date invalide)
 *
 * Pattern parité `SendQuoteRequestDialog` 9.L.6 / `ResolveEventDialog` 9.G.2.
 */

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { InsurerMultiSelect } from "../../../components/InsurerMultiSelect";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useCreateQuoteRequest } from "../../../lib/queries/quoteRequests";
import { toast } from "../../../lib/toast";

type Props = {
  readonly projectId: string;
  readonly onClose: () => void;
};

/** Convert ISO date input value (YYYY-MM-DD) to ISO datetime at noon UTC. */
function dateInputToIsoDatetime(value: string): string | undefined {
  if (!value) return undefined;
  return `${value}T12:00:00.000Z`;
}

export function CreateQuoteRequestDialog({ projectId, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const descId = useId();
  const titleFieldId = useId();
  const dateFieldId = useId();
  const insurersFieldId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  const create = useCreateQuoteRequest();
  const { reset: resetMutation } = create;

  const [titleValue, setTitleValue] = useState("");
  const [expectedResponseDate, setExpectedResponseDate] = useState("");
  const [selectedInsurers, setSelectedInsurers] = useState<string[]>([]);

  // Reset à chaque ouverture + focus initial Cancel.
  useEffect(() => {
    resetMutation();
    setTitleValue("");
    setExpectedResponseDate("");
    setSelectedInsurers([]);
    queueMicrotask(() => cancelBtnRef.current?.focus());
  }, [resetMutation]);

  // Esc → close (sauf pendant submit).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !create.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, create.isPending]);

  const onConfirm = () => {
    const isoDatetime = dateInputToIsoDatetime(expectedResponseDate);
    const trimmedTitle = titleValue.trim();
    create.mutate(
      {
        input: {
          project_id: projectId,
          ...(trimmedTitle ? { title: trimmedTitle } : {}),
          ...(isoDatetime ? { expected_response_by: isoDatetime } : {}),
          ...(selectedInsurers.length > 0
            ? {
                recipients: selectedInsurers.map((insurer_code) => ({
                  insurer_code,
                })),
              }
            : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success(t("quote_requests.create_dialog.success"));
          onClose();
        },
        // onError → bandeau inline (via create.error).
      },
    );
  };

  const apiErrorMsg =
    create.error instanceof ApiException
      ? getApiErrorMessage(create.error, lang) || t("errors.generic")
      : create.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!create.isPending) onClose();
        }}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-lg rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("quote_requests.create_dialog.title")}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <p id={descId} className="text-sm text-slate-700">
            {t("quote_requests.create_dialog.body")}
          </p>

          <div>
            <label
              htmlFor={titleFieldId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("quote_requests.create_dialog.title_label")}
            </label>
            <input
              id={titleFieldId}
              type="text"
              maxLength={200}
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              disabled={create.isPending}
              placeholder={t("quote_requests.create_dialog.title_placeholder")}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
            />
            <p className="mt-1.5 text-xs text-slate-500">
              {t("quote_requests.create_dialog.title_hint")}
            </p>
          </div>

          <div>
            <label
              htmlFor={dateFieldId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("quote_requests.create_dialog.expected_response_by_label")}
            </label>
            <input
              id={dateFieldId}
              type="date"
              value={expectedResponseDate}
              onChange={(e) => setExpectedResponseDate(e.target.value)}
              disabled={create.isPending}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
            />
            <p className="mt-1.5 text-xs text-slate-500">
              {t("quote_requests.create_dialog.expected_response_by_hint")}
            </p>
          </div>

          <div>
            <label
              htmlFor={insurersFieldId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("quote_requests.create_dialog.insurers_label")}
            </label>
            <InsurerMultiSelect
              id={insurersFieldId}
              value={selectedInsurers}
              onChange={(next) => setSelectedInsurers(next)}
              placeholder={t("quote_requests.create_dialog.insurers_placeholder")}
              disabled={create.isPending}
            />
            <p className="mt-1.5 text-xs text-slate-500">
              {t("quote_requests.create_dialog.insurers_hint")}
            </p>
          </div>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={create.isPending}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={create.isPending}
            aria-busy={create.isPending}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {create.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {create.isPending
              ? t("quote_requests.create_dialog.submitting")
              : t("quote_requests.create_dialog.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
